/**
 * SurfaceRulesSection — "Kinds of work": for every kind of work, its default and allowed policies, whether the
 * organization uses the system rule or its own, and the controls to change either.
 * Template example: NextAdmin `(dashboard)/(home)/_component/top-products` (ItemList in a Card) + demo
 * `/ui-elements/dialogs` for the editor.
 */
import type React from 'react';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  clearOrganizationRuntimePolicySurfaceRuleMutation,
  setOrganizationRuntimePolicySurfaceRuleMutation,
  setSystemRuntimePolicySurfaceRuleMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { RuntimePolicySurfaceRule, RuntimePolicySurfaceRuleWrite } from '../../../generated/api/types.gen.js';
import { Can } from '../../../components/auth/Can.js';
import { Badge, Button, Callout, Card, ItemList, Modal, SkeletonList } from '../../../components/ui/index.js';
import type { ItemListItem } from '../../../components/ui/index.js';
import { normalizeApiError } from '../../../services/api/errors.js';
import { runtimePoliciesQuery, runtimePolicySurfaceRulesQuery } from '../../../services/api/runtime-policy.queries.js';
import { useRefreshPolicies } from './policy-api.js';
import { SurfaceRuleEditor } from './SurfaceRuleEditor.js';
import { humanizeEnum } from './types.js';

type EditScope = 'organization' | 'system';

export const SurfaceRulesSection: React.FC = () => {
  const refresh = useRefreshPolicies();
  const rulesQuery = useQuery(runtimePolicySurfaceRulesQuery());
  const policiesQuery = useQuery(runtimePoliciesQuery());
  const [editing, setEditing] = useState<{ surface: RuntimePolicySurfaceRule['surface']; scope: EditScope } | null>(
    null,
  );

  const setSystemRule = useMutation(setSystemRuntimePolicySurfaceRuleMutation());
  const setOrganizationRule = useMutation(setOrganizationRuntimePolicySurfaceRuleMutation());
  const clearOrganizationRule = useMutation(clearOrganizationRuntimePolicySurfaceRuleMutation());

  const policies = policiesQuery.data ?? [];
  const activePolicies = policies.filter((p) => p.status === 'active');
  const nameOf = (id: number) => policies.find((p) => p.id === id)?.name ?? `Policy ${id}`;
  const rules = rulesQuery.data ?? [];
  const editingRule = editing ? rules.find((rule) => rule.surface === editing.surface) : undefined;

  const saveRule = async (
    scope: EditScope,
    surface: RuntimePolicySurfaceRule['surface'],
    body: RuntimePolicySurfaceRuleWrite,
  ) => {
    try {
      await (scope === 'system' ? setSystemRule : setOrganizationRule).mutateAsync({ path: { surface }, body });
    } catch (error) {
      throw normalizeApiError(error, 0);
    }
    setEditing(null);
    await refresh();
  };

  const clearRule = async (surface: RuntimePolicySurfaceRule['surface']) => {
    await clearOrganizationRule.mutateAsync({ path: { surface } });
    await refresh();
  };

  const items: ItemListItem[] = rules.map((rule) => {
    const own = rule.organizationId !== null;
    return {
      id: rule.surface,
      title: (
        <span className="flex items-center gap-2">
          {humanizeEnum(rule.surface)}
          <Badge variant={own ? 'accent' : 'neutral'}>{own ? 'Organization rule' : 'System rule'}</Badge>
        </span>
      ),
      subtitle: (
        <span className="block">
          Default: {nameOf(rule.defaultPolicyId)}. Allowed: {rule.allowedPolicyIds.map(nameOf).join(', ') || 'none'}
        </span>
      ),
      value: (
        <span className="flex flex-wrap items-center justify-end gap-2">
          <Can policy="CanManageOrgRuntimeFlags">
            <Button
              size="sm"
              variant="secondary"
              disabled={policiesQuery.isError}
              onClick={() => setEditing({ surface: rule.surface, scope: 'organization' })}
            >
              {own ? 'Edit our rule' : 'Set our own rule'}
            </Button>
            {own && (
              <Button
                size="sm"
                variant="ghost"
                loading={
                  clearOrganizationRule.isPending && clearOrganizationRule.variables?.path.surface === rule.surface
                }
                onClick={() => clearRule(rule.surface)}
              >
                Use system rule
              </Button>
            )}
          </Can>
          <Can policy="CanManageSystem">
            <Button
              size="sm"
              variant="secondary"
              disabled={policiesQuery.isError}
              onClick={() => setEditing({ surface: rule.surface, scope: 'system' })}
            >
              Edit system rule
            </Button>
          </Can>
        </span>
      ),
    };
  });

  return (
    <Card
      title="Kinds of work"
      actions={<span className="text-xs text-muted">Default and allowed policies for each</span>}
    >
      <div className="space-y-4">
        {rulesQuery.isLoading && <SkeletonList count={4} />}
        {rulesQuery.isError && <Callout tone="danger">Could not load the rules for the kinds of work.</Callout>}
        {policiesQuery.isError && (
          <Callout tone="danger">
            Could not load the policies: rules show policy numbers, and cannot be edited now.
          </Callout>
        )}
        {clearOrganizationRule.isError && (
          <Callout tone="danger">{normalizeApiError(clearOrganizationRule.error, 0).message}</Callout>
        )}
        {items.length > 0 && <ItemList items={items} aria-label="Kinds of work" />}
      </div>

      <Modal
        open={editing !== null && editingRule !== undefined}
        onClose={() => setEditing(null)}
        title={
          editing
            ? `${editing.scope === 'system' ? 'System' : 'Our'} rule: ${humanizeEnum(editing.surface).toLowerCase()}`
            : undefined
        }
        description="Pick the default policy and which other policies this kind of work may use."
      >
        {editing && editingRule && (
          <SurfaceRuleEditor
            key={`${editing.surface}-${editing.scope}`}
            rule={editing.scope === 'system' && editingRule.system ? editingRule.system : editingRule}
            // The system's rule holds system policies only; an organization's may hold its own too.
            policies={
              editing.scope === 'system'
                ? activePolicies.filter((policy) => policy.organizationId === null)
                : activePolicies
            }
            onSave={(next) => saveRule(editing.scope, editing.surface, next)}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
    </Card>
  );
};
